/**
 * Gift card activity tabs — state history and the audited reveal history
 * (transitions logged whenever the number/PIN is revealed). Each table is a
 * react-admin <Datagrid> (via the shared ActivityGrid) with TextField/DateField
 * columns instead of a hand-built MUI table.
 */
import { DateField, TextField } from 'react-admin';
import { Alert } from '@mui/material';

import { ActivityGrid, ActivityTabs, type ActivityTab } from '@src/components/indexPage';

import { REVEAL_TRANSITION_REASON } from './constants';
import type { GiftcardRecord, GiftcardStateTransition } from './types';

export const GiftcardActivityTabs = ({ gc }: { gc: GiftcardRecord }) => {
  // null is not the same as empty here, and collapsing them is dangerous on an audit surface.
  // The resolver's happy path always returns an array, so null means the field errored or was
  // denied — and `?? []` would hand ActivityTabs two zero-count tabs, which it hides along with
  // the whole zone. An auditor asking "who revealed this PIN?" would read that as "nobody".
  if (gc.stateTransitions == null) {
    console.error('Gift card activity could not be loaded', { giftcardId: gc.id });
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Activity and reveal history could not be loaded for this gift card.
      </Alert>
    );
  }

  // Elements are nullable too, not just the list — a null row would throw on `t.reason` and
  // crash the panel. The hand-written type used to hide that.
  const transitions = gc.stateTransitions.filter(
    (t): t is GiftcardStateTransition => t != null
  );
  const stateHistory = transitions.filter(t => t.reason !== REVEAL_TRANSITION_REASON);
  const revealHistory = transitions.filter(t => t.reason === REVEAL_TRANSITION_REASON);

  const tabs: ActivityTab[] = [
    {
      key: 'history',
      label: 'History',
      count: stateHistory.length,
      render: () => (
        <ActivityGrid rows={stateHistory} nowrapColumns={['createdAt']}>
          <DateField source="createdAt" label="Timestamp" showTime emptyText="—" />
          <TextField source="changeType" label="Type" emptyText="—" />
          <TextField source="changeFrom" label="From" emptyText="—" />
          <TextField source="changeTo" label="To" emptyText="—" />
          <TextField source="reason" label="Reason" emptyText="—" />
          <TextField source="user.name" label="User" emptyText="—" />
        </ActivityGrid>
      ),
    },
    {
      key: 'reveals',
      label: 'Reveal History',
      count: revealHistory.length,
      render: () => (
        <ActivityGrid rows={revealHistory} nowrapColumns={['createdAt']}>
          <DateField source="createdAt" label="Timestamp" showTime emptyText="—" />
          <TextField source="note" label="Field" emptyText="—" />
          <TextField source="user.name" label="User" emptyText="—" />
        </ActivityGrid>
      ),
    },
  ];

  return <ActivityTabs tabs={tabs} />;
};

export default GiftcardActivityTabs;
